import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Users, CheckCircle2, AlertTriangle, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/apiClient";
import { toast } from "sonner";

const STAGE_LABEL = { applied: "Applied", prescreen: "AI Pre-screen", documents: "Documents", interview: "Interview", training: "Training", human_review: "Human Review", onboarding: "Onboarding", hired: "Hired", rejected: "Rejected" };
const FLOW = ["prescreen", "documents", "interview", "training", "human_review", "onboarding"];

export default function Recruiting() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [apps, setApps] = useState([]);
  const [stages, setStages] = useState([]);
  const [filter, setFilter] = useState("");

  const load = useCallback(() => {
    const params = filter ? { stage: filter } : {};
    api.get("/recruiting/applicants", { params }).then((r) => { setApps(r.data.applicants); setStages(r.data.stages); }).catch(() => toast.error("Could not load applicants"));
  }, [filter]);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/login"); return; }
    if (!["admin", "executive", "manager", "staff"].includes(user.role)) { navigate("/dashboard"); return; }
    load();
  }, [user, loading, navigate, load]);

  const advance = async (id, stage) => {
    try {
      const { data } = await api.patch(`/recruiting/applicants/${id}`, { stage });
      if (data.requires_approval) toast.success(data.message);
      else toast.success(`Moved to ${STAGE_LABEL[stage]}`);
      load();
    } catch { toast.error("Update failed"); }
  };

  const uploadDoc = async (id, docName, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("doc_name", docName);
    fd.append("file", file);
    try { await api.post(`/recruiting/applicants/${id}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success(`${docName} received`); load(); }
    catch { toast.error("Upload failed"); }
  };

  if (loading || !user) return <div className="py-32 grid place-items-center"><Loader2 className="w-8 h-8 animate-spin text-blue-800" /></div>;

  const readyCount = apps.filter((a) => a.stage === "human_review").length;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="recruiting-page">
      <div className="flex items-center gap-2 mb-1"><Users className="w-5 h-5 text-teal-500" /><p className="text-xs uppercase tracking-[0.16em] text-teal-600 font-semibold">T5 Recruiting Agent</p></div>
      <h1 className="font-display text-3xl lg:text-4xl font-bold text-blue-950">Caregiver pipeline</h1>
      <p className="text-slate-600 mt-1">AI pre-screens, collects documents & prepares summaries. Hiring decisions route to the Approval Center.</p>

      <div className="mt-5 flex items-center gap-3 flex-wrap">
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-2 text-sm text-amber-800" data-testid="recruiting-ready-banner"><span className="font-bold">{readyCount}</span> ready for human review</div>
        <select value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="recruiting-filter" className="h-9 rounded-lg border border-slate-200 px-3 text-sm">
          <option value="">All stages</option>
          {stages.map((s) => <option key={s} value={s}>{STAGE_LABEL[s]}</option>)}
        </select>
      </div>

      <div className="mt-6 space-y-3">
        {apps.length === 0 && <div className="text-center py-16 text-slate-400" data-testid="recruiting-empty">No applicants.</div>}
        {apps.map((a) => {
          const pre = a.prescreen || {};
          const idx = FLOW.indexOf(a.stage);
          const next = idx >= 0 && idx < FLOW.length - 1 ? FLOW[idx + 1] : null;
          return (
            <div key={a.id} className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm" data-testid={`applicant-card-${a.id}`}>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex-1 min-w-[240px]">
                  <div className="flex items-center gap-2">
                    <h3 className="font-display text-lg font-bold text-blue-950">{a.name}</h3>
                    <span className="text-[10px] uppercase font-semibold px-2 py-0.5 rounded-full bg-blue-50 text-blue-700">{STAGE_LABEL[a.stage]}</span>
                    {a.stage === "human_review" && <span className="text-[10px] uppercase font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">needs decision</span>}
                  </div>
                  <p className="text-sm text-slate-500 mt-0.5">{a.city} · {a.email} · {a.phone}</p>
                  <p className="text-sm text-slate-700 mt-2">{pre.summary}</p>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {(pre.strengths || []).map((s, i) => <span key={i} className="inline-flex items-center gap-1 text-xs bg-teal-50 text-teal-700 rounded-full px-2 py-0.5"><CheckCircle2 className="w-3 h-3" /> {s}</span>)}
                    {(pre.flags || []).map((s, i) => <span key={i} className="inline-flex items-center gap-1 text-xs bg-amber-50 text-amber-700 rounded-full px-2 py-0.5"><AlertTriangle className="w-3 h-3" /> {s}</span>)}
                  </div>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {(a.documents || []).map((d, i) => (
                      <label key={i} className={`inline-flex items-center gap-1 text-xs rounded-full px-2 py-0.5 cursor-pointer ${d.status === "received" ? "bg-teal-50 text-teal-700" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`} data-testid={`applicant-doc-${a.id}-${i}`}>
                        <FileText className="w-3 h-3" /> {d.name}: {d.status}
                        {d.status !== "received" && <input type="file" className="hidden" accept=".pdf,.png,.jpg,.jpeg,.webp" onChange={(e) => uploadDoc(a.id, d.name, e.target.files?.[0])} data-testid={`applicant-doc-upload-${a.id}-${i}`} />}
                      </label>
                    ))}
                  </div>
                </div>
                <div className="text-center shrink-0">
                  <div className="grid place-items-center w-16 h-16 rounded-full bg-blue-950 text-white"><span className="font-display text-xl font-bold">{pre.score ?? "—"}</span></div>
                  <p className="text-[10px] text-slate-400 mt-1">pre-screen</p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-slate-100">
                {next && <Button onClick={() => advance(a.id, next)} data-testid={`applicant-advance-${a.id}`} className="bg-blue-900 hover:bg-blue-950 h-9 text-sm">Advance → {STAGE_LABEL[next]}</Button>}
                {["human_review", "onboarding"].includes(a.stage) && <Button onClick={() => advance(a.id, "hired")} data-testid={`applicant-hire-${a.id}`} className="bg-teal-600 hover:bg-teal-700 h-9 text-sm">Recommend hire</Button>}
                {a.stage !== "hired" && a.stage !== "rejected" && <Button onClick={() => advance(a.id, "rejected")} data-testid={`applicant-reject-${a.id}`} variant="outline" className="h-9 text-sm border-red-200 text-red-600 hover:bg-red-50">Reject</Button>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
